import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { dataExportsAPI } from '../../../api';
import { MoonLoader } from 'react-spinners';
import DownloadIcon from '@mui/icons-material/Download';
import CustomModal from "../CustomModal/CustomModal"; // Import the modal component
import FieldEditor from './FieldEditor';
import MultiSelectDropdown from '../BusinessSelector/MultipleChoicesDropdown';
import '../../../styles/dataExports/index.css';
import '../../../styles/dataExports/SettingForm.css';

const DataExports = () => {
    // If the user is not logged in, redirect to the login page
    useEffect(() => {
        if (!localStorage.getItem('email')) window.location.href = '/auth/signin';
    }, []);

    const fetchUserResponse = useSelector(state => state.fetchUserResponse);

    const businesses = fetchUserResponse?.businesses;
    // Get role and business roles
    const role = localStorage.getItem('rbac_role');
    let businessRoles = [];
    try {
      businessRoles = JSON.parse(localStorage.getItem('rbac_data')) || [];
    } catch {
      businessRoles = [];
    }

    // Filter businesses based on role
    let businessNames = businesses?.map(business => Object.keys(business)[0]) || [];

    if (role?.toLowerCase() !== 'tla') {
      // For non-TLA users, filter based on businesses where they have non-technician roles
      const allowedBusinesses = businessRoles
        .filter(br => br.role?.toLowerCase() !== 'technician') // Exclude technician roles
        .map(br => br.business);
      
      businessNames = businessNames.filter(name => allowedBusinesses.includes(name));
    }

    businessNames = businessNames?.sort();
    
    const [selectedBusiness, setSelectedBusiness] = useState('');
    const [selectedSites, setSelectedSites] = useState([]);
    const [loadingTables, setLoadingTables] = useState(false);
    const [tableInfo, setTableInfo] = useState([]);
    const [activeMenuItem, setActiveMenuItem] = useState('analytics');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    
    
    const filteredSites = businesses?.filter(obj => obj[selectedBusiness] !== undefined);


    useEffect(() => {
        if (businessNames && !selectedBusiness) {
        setSelectedBusiness(businessNames[0]);
        }
    }, [businessNames]);

    const extractAndSortBySite = (filteredSites) => {
        return filteredSites
          .flatMap(item => {
            const key = Object.keys(item)[0]; // Get the key, e.g., "HIVE"
            const siteDataArray = item[key]; // Get all site data from that key (as an array)
            return siteDataArray.map(siteData => ({
                site: siteData.site,
                hwid: siteData.hwid
            }));
        }).sort((a, b) => a.site.localeCompare(b.site)); // Sort by the site property
    };



    // Use useMemo to memorize the sorted sites
    const sortedSites = useMemo(() => {
        if (filteredSites) {
            return extractAndSortBySite(filteredSites);
        }
        return []; // Return an empty array if no filteredSites
    }, [selectedBusiness]); // Recompute when filteredSites changes
    

    const handleBusinessChange = (event) => {
        setSelectedBusiness(event.target.value);
        handleSiteSelectionChange([]);
        setSelectedSites([]);
        
        setSelectedTables([]);
        setCustomSelectedTables([]);

        setSelectAll(false);
        setStartDate("");
        setEndDate("");
        setTableInfo([]);
      };


      
      const getTables = async (business_name) => {
        setLoadingTables(true);
        try {
            let res = await dataExportsAPI.post('/tables', {business_name, selectedSites});

            if (res.status === 200 || res.status === 500) {
                let data = res.data.data;
                data = data.map(site=> ({...site, tables: site.tables.map(table=>({...table, columns: table.columns.map(column=>({...column, columnName: column.field}))}))}))

                if (selectedSites.length > 1) {
                    // Create a Set to hold unique table names
                    let uniqueTableNames = new Set();
                    // Create an array to store unique tables
                    let uniqueTables = [];

                    // Iterate over the list of objects
                    data.forEach(obj => {
                        obj.tables.forEach(table => {
                            if (!uniqueTableNames.has(table.name)) {
                            uniqueTableNames.add(table.name); // Add table name to the Set
                            uniqueTables.push(table); // Add the table to the result array
                        }});
                    });

                    setTableInfo(uniqueTables);

                } else {
                    setTableInfo(data[0].tables);
                }

            } else {
                toast.error("Failed to get tables");
            }

        } catch (error) {
            // console.log("error", error);
        }
        setLoadingTables(false);

    }
    
    useMemo(() => {
        if (selectedSites.length > 0 && selectedBusiness !== '') {
            getTables(selectedBusiness, selectedSites);
        }
    }, 
    [selectedSites, selectedBusiness]);

    const getDatetimeNow = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      return `${year}${month}${day}${hours}${minutes}${seconds}`;
    }

    const handleDownload = async () => {
      try {
        let hw_ids = selectedSites.map(site => site.hwid);
        
        let data_passed = {
          hw_ids: hw_ids,
          customSelectedTables: customSelectedTables,
          startDate: startDate,
          endDate: endDate,
        };
    
        // Check if there's data available
        const checkResponse = await dataExportsAPI.post("/check-data-availability", data_passed);
        
        // Display information about partial data if applicable
        if (checkResponse.data.tablesWithData.length < checkResponse.data.totalTables) {
          // Optional: Show a confirmation dialog for partial download
          const tablesWithoutData = checkResponse.data.totalTables - checkResponse.data.tablesWithData.length;
          const confirmDownload = window.confirm(
            `${tablesWithoutData} out of ${checkResponse.data.totalTables} selected tables have no data and will be excluded. Do you want to proceed with downloading the available data?`
          );
          
          if (!confirmDownload) {
            return; // User cancelled the download
          }
        }
        
        // Proceed with download
        const response = await dataExportsAPI.post("/download-csv", 
          data_passed, 
          { 
            responseType: "blob",
            validateStatus: function(status) {
              return status >= 200 && status < 300 || status === 404; // Also accept 404
            }
          }
        );
        
        // Check if the response is JSON (error) instead of a blob
        const contentType = response.headers['content-type'];
        if (contentType && contentType.includes('application/json')) {
          // Convert blob to text and parse as JSON
          const reader = new FileReader();
          reader.onload = function() {
            try {
              const errorData = JSON.parse(reader.result);
              toast.error(errorData.message || "Error processing your request");
            } catch (e) {
              toast.error("Error processing the response");
            }
          };
          reader.readAsText(response.data);
          return;
        }
        
        // Process the blob for download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${getDatetimeNow()}-shogun-data.zip`);
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);
        }, 100);
        
      } catch (error) {
        if (error.response) {
          // Handle specific status codes
          if (error.response.status === 404) {
            toast.error(error.response.data.message || "No data found for the selected criteria");
          } else {
            toast.error(error.response.data.message || "Error processing your request");
          }
        } else {
          // Network or other errors
          toast.error("Error downloading data. Please try again later.");
          console.error("Error downloading CSV:", error);
        }
      }
    };



    const [selectedTables, setSelectedTables] = useState([]);
  
    // State to manage the "Select All" checkbox
    const [selectAll, setSelectAll] = useState(false);
  
    // Handle individual checkbox selection
    const handleCheckboxChange = (tableObj) => {
      setSelectedTables((prevSelectedTables) => {
        const isSelected = prevSelectedTables.map(t=>t.name).includes(tableObj.name);

        const newSelectedTables = isSelected
          ? prevSelectedTables.filter((t) => t.name !== tableObj.name) // Deselect
          : [...prevSelectedTables, tableObj]; // Select
  
        // Check if all tables are selected
        setSelectAll(newSelectedTables.length === tableInfo.length);
        return newSelectedTables;
      });

      setCustomSelectedTables((prevSelectedTables) => {
        const isSelected = prevSelectedTables.map(t=>t.name).includes(tableObj.name);

        const newSelectedTables = isSelected
          ? prevSelectedTables.filter((t) => t.name !== tableObj.name) // Deselect
          : [...prevSelectedTables, tableObj]; // Select
  
        // Check if all tables are selected
        setSelectAll(newSelectedTables.length === tableInfo.length);
        return newSelectedTables;
      });
    };
  
    // Handle "Select All" checkbox change
    const handleSelectAllChange = () => {
      if (selectAll) {
        setSelectedTables([]); // Deselect all
        setCustomSelectedTables([]);
      } else {
        setSelectedTables(tableInfo.map((t) => t)); // Select all
        // Keep the tables in customSelectedTables, add new tables to it
        setCustomSelectedTables((prevSelectedTables) => {
          return [...prevSelectedTables, ...tableInfo];
        });
      }
      setSelectAll(!selectAll); // Toggle the "Select All" state
    };

    const selectedCount = selectedTables.length;

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [showModal, setShowModal] = useState(false);
    const [tablePoppedUp, setTablePoppedUp] = useState({}); //advanced table info
    const [selectedColumnObjs, setSelectedColumnObjs] = useState([]);
    const [customSelectedTables, setCustomSelectedTables] = useState([]);

    const handleSiteSelectionChange = (selected) => {
      setSelectedSites(selected);
    };

    const isTableChecked = (tableObj) => {
      return (selectedTables && selectedTables.length !== 0) ? selectedTables.map((t)=>t.name).includes(tableObj.name) : false
    }

    const handleOpenAdvanced = (tableObj) => {
      setTablePoppedUp(tableObj);
      // find table in customSelectedTables with the same name as tableObj
      let table = customSelectedTables.find(t => t.name === tableObj.name);
      if (table) setSelectedColumnObjs(table.columns)
      else setSelectedColumnObjs(tableObj.columns);
      setShowModal(true)
    }

    return (
      <>
        <div className='setting-form-container'>
          <div className='setting-form-header'>
            <h2>CSV Data Exports</h2>
            <div className='setting-form-header-actions'>
              <button className='setting-form-btn-update' onClick={handleDownload} 
                      disabled={startDate==="" || endDate ==="" || selectedCount === 0}>
                          <DownloadIcon/>DOWNLOAD</button>
            </div>
          </div>
            
            <div className='setting-form-config-section'>
                <h3>Data Source</h3>
                <div className='setting-form-input-group'>
                  <div style={{marginBottom: "10px"}}>
                      <label style={{display: "block"}}>Select a business:</label>
                      <select className='setting-form-dark-select' value={selectedBusiness} 
                        onChange={(event)=>{handleBusinessChange(event)}} disabled={loadingTables}>
                      {businessNames?.map((business, index) => (
                          <option key={index} value={business}>{business}</option>
                      ))}
                      </select>
                  </div>
                </div>
                
                <div className='setting-form-input-group'>
                  <label>Select sites:</label>
                  <MultiSelectDropdown 
                    sites={sortedSites} 
                    onChange={handleSiteSelectionChange} 
                    loading={loadingTables} 
                    selectedSites={selectedSites}
                    setSelectedSites={setSelectedSites}
                  />
                </div>
                
                <div >
                    <MoonLoader loading={loadingTables} size={16} color={"white"} />
                </div>
            </div>

            <div className='setting-form-config-section'>
              <h3>Date Range</h3>
              <DateRangePicker 
                  startDate={startDate} 
                  endDate={endDate} 
                  setStartDate={setStartDate} 
                  setEndDate={setEndDate}
              />
            </div>
            

            <CustomModal
              show={showModal}
              handleClose={() => {
                setShowModal(false);
                setCustomSelectedTables(customSelectedTables.map(table => table.name === tablePoppedUp.name ? { ...table, columns: selectedColumnObjs } : table));
                setSelectedColumnObjs([]);
              }}
              title={tablePoppedUp.name}
            >
              {
                <FieldEditor tablePoppedUp={tablePoppedUp}
                selectedColumnObjs={selectedColumnObjs} setSelectedColumnObjs={setSelectedColumnObjs} />
              }

            </CustomModal>
            
            <div className='setting-form-config-section'>
              <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                <h3>Select Tables</h3>
              </div>
              <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                  <div style={{marginBottom: "10px"}}>
                    <input
                        type="checkbox"
                        id="selectAll"
                        name="selectAll"
                        value="selectAll"
                        checked={selectAll}
                        onChange={handleSelectAllChange}
                        disabled={!tableInfo || tableInfo.length === 0}
                    />
                      <label htmlFor="selectAll"> Select All</label>
                  </div>
                  <div className='setting-form-table-count'>
                    <div style={{marginRight:"5px"}}>{selectedCount} tables selected</div>
                  </div>
              </div>

              <div>
                  {
                      loadingTables ? (
                          <div>Loading tables...</div>
                      ) : tableInfo?.length === 0 ? (
                          <div>No tables found</div>
                      ) : (
                          <div className='setting-form-tables-grid'>
                            {tableInfo?.map((tableObj, index) => (
                                <div key={index} className='setting-form-table-item'>
                                  <div>
                                    <input
                                      type="checkbox"
                                      id={tableObj.name}
                                      name={tableObj.name}
                                      value={tableObj.name}
                                      checked={isTableChecked(tableObj)}
                                      onChange={() => handleCheckboxChange(tableObj)}
                                    />
                                    <label htmlFor={tableObj.name}>{tableObj.name}</label>
                                  </div>
                                  {
                                    <span className='setting-form-table-tag'
                                    onClick={(e)=>{e.stopPropagation(); handleOpenAdvanced(tableObj)}}>Advanced</span>
                                  }
                                </div>
                            ))}
                          </div>
                      )
                  }
              </div>
            </div>
        </div>
        </>
    )
}



const DateRangePicker = ({ startDate, endDate, setStartDate, setEndDate}) => {
    // Get today's date and calculate 2 years ago
    const today = new Date();
    const twoYearsAgo = new Date(today);
    twoYearsAgo.setFullYear(today.getFullYear() - 2);
  
    // Format the dates as YYYY-MM-DD
    const formattedToday = today.toISOString().split('T')[0];
    const formattedTwoYearsAgo = twoYearsAgo.toISOString().split('T')[0];
  
    // Handle start date change
    const handleStartChange = (e) => {
      const newStartDate = e.target.value;
      setStartDate(newStartDate);
  
      // Ensure end date is always on or after the start date
      if (new Date(newStartDate) > new Date(endDate)) {
        setEndDate(newStartDate);
      }
    };
  
    // Handle end date change
    const handleEndChange = (e) => {
      setEndDate(e.target.value);
    };
  
    return (
      <div>
          <div style={{display: "flex", justifyContent: "space-between", width: "100%"}}>
            <div  className='setting-form-input-group' style={{flex:1, marginRight: "10px"}}>
              <label htmlFor="start" style={{display:"block"}}>Start date:</label>
              <input className='setting-form-dark-select' 
                type="date"
                id="start"
                name="start"
                value={startDate}
                min={formattedTwoYearsAgo} // Min start date is 2 years ago
                max={formattedToday} // Max start date is today
                onChange={handleStartChange}
              />
            </div>
            <div  className='setting-form-input-group' style={{flex:1}}>
              <label htmlFor="end" style={{display:"block"}}>End date:</label>
              <input className='setting-form-dark-select' 
                type="date"
                id="end"
                name="end"
                value={endDate}
                min={startDate} // Min end date is the selected start date
                max={formattedToday} // Max end date is today
                onChange={handleEndChange}
              />
            </div>
          </div>
          <div className='setting-form-header-actions'>
            <button className='setting-form-btn-update' onClick={()=>{setStartDate(""); setEndDate("")}}>Clear</button>
          </div>

      </div>
    );
  };


export default DataExports;

